// Type-only import: the dashboard never bundles server code.
import type { AppRouter } from "@loot-os/functions/src/dashboard-api/trpc/router.js";
import type { inferOutput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";

type OrdersProxy = TRPCOptionsProxy<AppRouter>["platform"]["fulfillment"]["orders"];

/** One timeline entry as it reaches the browser (dates as ISO strings). */
export type OrderActivityEntry = inferOutput<OrdersProxy["activity"]>["entries"][number];

/** The procedures and routes an order's audit rows carry, in a fulfiller's words. */
const ACTION_LABELS: Record<string, string> = {
  "platform.fulfillment.orders.transition": "Moved",
  "platform.fulfillment.orders.update": "Moved",
  "platform.fulfillment.orders.cancel": "Cancelled the order",
  "platform.fulfillment.orders.cancelLine": "Cancelled a line",
  "platform.fulfillment.orders.updateLines": "Updated lines",
  "platform.fulfillment.orders.updateAddress": "Changed the shipping address",
  "platform.fulfillment.orders.updateMetadata": "Edited the order",
  "POST /orders": "Created the order",
  "POST /orders/{id}/lines": "Added lines",
  "POST /orders/{id}/cancel": "Cancelled the order",
  "DELETE /orders/{id}": "Deleted the order",
  "POST /orders/{id}/shipping-fee-refunded": "Recorded the shipping fee refund",
  "POST /orders/{id}/processing": "Moved to PROCESSING",
  "POST /orders/{id}/preship": "Moved to PRESHIPPED",
  "POST /orders/{id}/ship": "Moved to SHIPPED",
  "POST /orders/{id}/approve": "Approved",
  "POST /orders/{id}/hold": "Put on hold",
  "PUT /orders/{id}/shipping-address": "Changed the shipping address",
  "PATCH /orders/{id}": "Edited the order",
  "POST /v1/fulfillment/orders": "Requested the withdrawal",
  "POST /v1/fulfillment/orders/:orderId/cancel": "Cancelled the order",
};

/**
 * What happened, in words: a status entry names the status reached; an audited write its action,
 * with the target status when it moved the order. An action not in the list shows as it is stored,
 * so a new route is never hidden.
 */
export function describeOrderActivity(entry: OrderActivityEntry): string {
  if (entry.source === "status") return entry.status ? `Reached ${entry.status}` : "Order created";
  const action = entry.action ?? "";
  const label = ACTION_LABELS[action] ?? action;
  const target = entry.input?.toStatus ?? entry.input?.status;
  if (label === "Moved" && typeof target === "string") return `Moved to ${target}`;
  if (action === LINE_UPDATES_ACTION) return describeLineUpdates(entry.input?.updates) ?? label;
  return label;
}

const LINE_UPDATES_ACTION = "platform.fulfillment.orders.updateLines";

/** "Packed 2 cards" or "Marked 1 card pending" when every update sets one status; else null. */
function describeLineUpdates(value: unknown): string | null {
  const updates = parseLineUpdates(value);
  const statuses = new Set(updates?.map((update) => update.status));
  if (!updates?.length || statuses.size !== 1) return null;
  const cards = `${updates.length} ${updates.length === 1 ? "card" : "cards"}`;
  if (statuses.has("PACKED")) return `Packed ${cards}`;
  if (statuses.has("PENDING")) return `Marked ${cards} pending`;
  return null;
}

/** One `orders.updateLines` update as the audit row stored it: one selector, and the status set. */
interface StoredLineUpdate {
  ref: string | undefined;
  status: string | undefined;
}

/**
 * The `updates` array of an audited `orders.updateLines` input, or null when it is not one. The
 * row is stored jsonb, so each element is checked rather than cast; a line is named by whichever
 * selector the write used.
 */
function parseLineUpdates(value: unknown): StoredLineUpdate[] | null {
  if (!Array.isArray(value)) return null;
  return value.map((item): StoredLineUpdate => {
    const record =
      item !== null && typeof item === "object" ? (item as Record<string, unknown>) : {};
    const ref = [record.lineId, record.itemId, record.inventoryItemId].find(
      (candidate): candidate is string => typeof candidate === "string",
    );
    return { ref, status: typeof record.status === "string" ? record.status : undefined };
  });
}

/** Who did it, in words; null for a status entry, which has no actor. */
export function describeOrderActivityActor(entry: OrderActivityEntry): string | null {
  const actor = entry.actor;
  if (!actor) return null;
  if (actor.type === "admin") return actor.name ?? "Staff";
  if (actor.type === "api_key") return `Tenant API${actor.name ? ` (${actor.name})` : ""}`;
  if (actor.type === "system") return actor.name ?? "Loot OS";
  if (actor.type === "end_user") return "Shopper";
  return actor.name ?? actor.type;
}

/** Keys the timeline shows beside an entry; ids repeat what the page already shows. */
const HIDDEN_INPUT_KEYS = new Set(["id", "orderId", "lineId", "toStatus", "status", "fromStatus"]);

/**
 * The write's input as `key: value` pairs, leaving out what the description already says. A line
 * update's `updates` reads as the cards it touched, named from `lineNames` (line id to card name),
 * rather than as the stored JSON of line ids.
 */
export function orderActivityInputPairs(
  entry: OrderActivityEntry,
  lineNames: Record<string, string>,
): [string, string][] {
  return Object.entries(entry.input ?? {})
    .filter(([key, value]) => !HIDDEN_INPUT_KEYS.has(key) && value !== null && value !== "")
    .map(([key, value]): [string, string] => {
      const updates = key === "updates" ? parseLineUpdates(value) : null;
      if (updates) {
        const cards = updates.map(
          (update) =>
            `${(update.ref && lineNames[update.ref]) ?? update.ref ?? "a line"} → ${update.status ?? "?"}`,
        );
        return ["cards", cards.join(", ")];
      }
      return [key, typeof value === "string" ? value : JSON.stringify(value)];
    });
}
